import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useForm } from '@tanstack/react-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import type { InterestWorkspaceResponse } from 'types/api-responses';

import {
  confirmPanelStateAtom,
  confirmSlotAtom,
  slotListAtomFamily,
  workspaceAtomFamily,
  type ConfirmSlotResponse,
  type ConfirmTractEntry,
} from './atoms';
import type { ConfirmationForm, SlotInterestRow } from './types';
import { useActiveRowStale } from './use-active-row-stale';
import {
  EMPTY_PER_TRACT_DRAFT,
  buildTractsPayload,
  initialTractDrafts,
  rowKey,
} from './utils';

interface ContextValue {
  form: ConfirmationForm;
  activeRow: SlotInterestRow | null;
  /** INS-11586: true when a confirmation event has landed but no successful
   *  refetch has settled since, so `activeRow` predates the user's last edit.
   *  The panel blocks interaction rather than render reviews that may already
   *  be gone. */
  activeRowStale: boolean;
  /** Current workspace id, derived from the route's `:id` param. */
  workspaceId: number;
  /** Current workspace record — guaranteed loaded (the provider only mounts
   *  after the shell's loadable resolves). */
  workspace: InterestWorkspaceResponse;
  /** Submits one-or-more tract entries for the active row (one entry for a
   *  per-tract Confirm, the full remainder for Confirm All Remaining). The
   *  whole array is atomic on the BE. Resolves with the updated row, or
   *  throws — so the panel surfaces the outcome from the awaited result. */
  submitTracts: (entries: ConfirmTractEntry[]) => Promise<ConfirmSlotResponse>;
}

const WorkspaceConfirmationContext = createContext<ContextValue | null>(null);

/**
 * Owns the per-tract Confirm Interest form for the whole workspace-shell
 * session (not just while the panel is open), so closing and re-opening the
 * panel for the same row preserves the user's typed values. Also surfaces the
 * workspace identity so Group C components don't prop-drill `workspaceId`.
 *
 * @param {object} props - Component props.
 * @param {ReactNode} props.children - The shell subtree that consumes the context.
 * @return {JSX.Element} The context provider wrapping `children`.
 * @throws {Error} If mounted before the workspace record has loaded.
 */
export const WorkspaceConfirmationProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  // Workspace identity comes from the route — the same `:id` the shell reads.
  const { id } = useParams<{ id: string }>();
  const workspaceId = Number(id);
  const { data: workspaceData } = useAtomValue(
    workspaceAtomFamily(workspaceId)
  );
  const panelState = useAtomValue(confirmPanelStateAtom);
  const confirmSlot = useSetAtom(confirmSlotAtom);
  // The Comparison Table's slot list — the same atom the table reads. Tracking
  // it here keeps the panel's row LIVE: a hold / release / comment mutation
  // refetches this list (via `invalidateAfterConfirmationEvent`), and the
  // derived `activeRow` below repaints the open panel with the fresh hold
  // overlay the moment that refetch lands — instead of staying frozen on the
  // snapshot captured when the panel opened.
  const slotList = useAtomValue(
    slotListAtomFamily({ surface: 'comparison', workspaceId })
  );

  // The row the panel opened for, as captured in the open-state atom. Identity
  // (workspaceSlotId + streams) is stable across a refetch, but its hold /
  // certification fields go stale once a mutation lands.
  const snapshotRow = panelState?.mode === 'single' ? panelState.rows[0] : null;
  const activeRowKey = snapshotRow ? rowKey(snapshotRow) : null;

  // INS-11586 (freshness): true when a confirmation event has landed but no
  // successful refetch has settled since — every row on hand predates the
  // user's edit, whether it came from the list or the open-time snapshot.
  const activeRowStale = useActiveRowStale(
    slotList.loading === true,
    slotList.error
  );

  // Prefer the live row matching the snapshot's identity so the panel reflects
  // post-mutation state (held block, Release Hold) without a close/re-open;
  // fall back to the snapshot when the row isn't in the current page/filter.
  //
  // Deliberately still returns a row while `activeRowStale` — nulling it here
  // would unmount the tract sections and discard the user's in-progress draft
  // on any transient refetch, which is worse than the staleness. The panel
  // instead reads `activeRowStale` and blocks interaction until the row is
  // known-current.
  //
  // Known bound on the match (INS-11586, out of scope here): the identity is
  // (workspaceSlotId, streams[]), and the BE consolidates a slot's per-stream
  // rows into buckets keyed on the SLOT-LEVEL confirmation fingerprint
  // (`confirmation_bucket_key`). A mutation that changes that fingerprint for
  // only SOME of the slot's streams splits (or merges) the bucket, so the
  // row's `streams[]` — and with it the rowKey — moves. `fresh` then misses,
  // this falls back to `snapshotRow`, and `activeRowStale` is false because the
  // refetch itself succeeded: a silent pre-mutation row, with no banner.
  //
  // It cannot produce the review-resurrection class this ticket is about. Only
  // the NULL-subtract (whole-slot) confirmation feeds that bucket key — the
  // per-tract certifies the panel issues are subtract-grain, and the derived
  // slot-level rollup they produce is attached AFTER consolidation. The panel's
  // only NULL-subtract writer is a certify on a slot with NO subtracts
  // (`SingleSlotContent` targets `[null]` only in that case), and a slot with no
  // subtracts has no hold UI at all — both "Mark for Review" and
  // `TractHoldSection` are gated on `subtract !== null`. So the split path and
  // the hold path are disjoint. What's left is a no-subtract slot showing its
  // pre-mutation values after a partial-stream certify, where the panel's own
  // `sessionCertified` state already reflects the submit and re-opening picks up
  // the new identity. Widening the match (slot-grain, or re-pointing the open
  // panel at the new row) is a follow-up, not a fix for this bug.
  const activeRow = useMemo(() => {
    if (!snapshotRow) return null;
    const fresh = slotList.data?.results?.find(
      (row) => rowKey(row) === activeRowKey
    );
    return fresh ?? snapshotRow;
  }, [snapshotRow, activeRowKey, slotList.data?.results]);

  // Tracks the row the form was last seeded for, so re-opening the panel for
  // the SAME row preserves the draft while a DIFFERENT row re-seeds. A
  // per-tract submit refetches the row but keeps the same rowKey, so sibling
  // tracts' unsubmitted drafts survive the refresh.
  const lastSeededRowKey = useRef<string | null>(null);

  // The form owns values only — no TanStack `onSubmit`/`validators`.
  // `canSubmitTract` is a validity helper the tract sections call directly;
  // submission goes through `submitTracts` below.
  const form = useForm({
    defaultValues: EMPTY_PER_TRACT_DRAFT,
  }) as unknown as ConfirmationForm;

  // The panel drives submission through this (not TanStack's `handleSubmit`).
  // It awaits the mutation and returns the updated row so callers surface
  // success/failure deterministically. Calls `confirmSlotAtom` directly
  // rather than via `useConfirmationMutations` (that hook reads THIS context,
  // so it can't run here). Does NOT reset the whole form — only the submitted
  // tracts become certified summaries; sibling drafts persist.
  const submitTracts = useCallback(
    (entries: ConfirmTractEntry[]): Promise<ConfirmSlotResponse> => {
      if (!activeRow) {
        return Promise.reject(new Error('No active row to confirm.'));
      }
      return confirmSlot(
        workspaceId,
        activeRow.workspaceSlotId,
        buildTractsPayload(entries)
      );
    },
    [activeRow, workspaceId, confirmSlot]
  );

  // Re-seed when the panel opens for a different row; preserve the draft when
  // re-opening for the same row.
  useEffect(() => {
    if (!activeRow) return;
    if (activeRowKey !== lastSeededRowKey.current) {
      form.reset(initialTractDrafts(activeRow));
      lastSeededRowKey.current = activeRowKey;
      return;
    }
    // Same row identity, but its per-subtract rows can arrive (or grow) AFTER
    // the initial seed — the comparison list enriches subtracts post-paint and
    // rowKey (slot + streams) doesn't change when that happens, so the seed
    // above never re-runs. Seed any per-tract drafts that are still missing;
    // otherwise those editor cards have no draft and go inert (checkboxes,
    // source radios, and Certify all dead). Existing drafts are left untouched
    // so an in-progress edit survives a refetch.
    const existing = form.store.state.values.tracts;
    const seeded = initialTractDrafts(activeRow).tracts;
    for (const [tractDraftKey, draft] of Object.entries(seeded)) {
      if (!(tractDraftKey in existing)) {
        form.setFieldValue(`tracts.${tractDraftKey}`, draft as never);
      }
    }
  }, [activeRow, activeRowKey, form]);

  if (!workspaceData || 'error' in workspaceData) {
    throw new Error(
      "WorkspaceConfirmationProvider mounted before the workspace loaded — wrap it inside the shell's loaded branch"
    );
  }

  return (
    <WorkspaceConfirmationContext.Provider
      value={{
        form,
        activeRow,
        activeRowStale,
        workspaceId,
        workspace: workspaceData,
        submitTracts,
      }}
    >
      {children}
    </WorkspaceConfirmationContext.Provider>
  );
};

/**
 * Reads the workspace-confirmation context.
 *
 * @return {ContextValue} The form + active row + workspace identity.
 * @throws {Error} If used outside `<WorkspaceConfirmationProvider>`.
 */
export const useWorkspaceConfirmation = (): ContextValue => {
  const ctx = useContext(WorkspaceConfirmationContext);
  if (!ctx) {
    throw new Error(
      'useWorkspaceConfirmation must be used inside <WorkspaceConfirmationProvider>'
    );
  }
  return ctx;
};
